import { useState } from 'react'
import { toast } from 'sonner'

import {
  useCreateStaff,
  useDeleteStaff,
  useResetStaffPassword,
  useStaff,
  type OwnerStaffRow,
} from '../hooks/useStaff.js'
import { useBranches } from '../hooks/useBranches.js'
import { useOwnerSession } from '../hooks/useOwnerSession.js'
import { OwnerApiError } from '../ownerApi.js'
import type { OwnerBranch } from '../types.js'

/**
 * Slice 4b iteration — owner-side staff CRUD. List + add + delete + reset
 * password. Seat usage ("3 of 5 used") + add button gated on
 * tenants.max_staff (set by admin). On reset / create the temp password
 * surfaces in a modal exactly once — owner relays it via Messenger/Viber.
 */
export function StaffPage() {
  const staffQ = useStaff()
  const branchesQ = useBranches()
  const { data: me } = useOwnerSession()
  const tenant = me?.tenant
  const [addOpen, setAddOpen] = useState(false)
  const [tempPwd, setTempPwd] = useState<{
    email: string | null
    name: string
    password: string
  } | null>(null)

  if (staffQ.isLoading || branchesQ.isLoading) {
    return <p className="owner-loading">Loading…</p>
  }
  if (staffQ.error) {
    return <p className="owner-error">Couldn't load staff. Try refreshing.</p>
  }

  const staffRows = staffQ.data?.staff ?? []
  const seats = staffQ.data?.seats ?? { count: 0, cap: 0 }
  const atCap = seats.count >= seats.cap
  const branches = (branchesQ.data?.branches ?? []).filter(
    (b) => b.archived_at === null,
  )

  return (
    <div>
      <h1>Staff</h1>
      <p className="owner-greeting-sub">
        Staff log in at the staff URL below and stamp customer cards on their
        phone.
      </p>

      {tenant && <StaffAccessCard slug={tenant.slug} />}

      <div className="owner-card">
        <div className="owner-card-title">
          Seats: {seats.count} of {seats.cap} used
        </div>
        <p className="owner-card-sub">
          {atCap
            ? "You've hit your staff seat limit. Contact us to upgrade your plan."
            : `You can add ${seats.cap - seats.count} more staff member${
                seats.cap - seats.count === 1 ? '' : 's'
              }.`}
        </p>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={() => setAddOpen(true)}
          disabled={atCap || branches.length === 0}
          title={
            branches.length === 0
              ? 'Add a branch first. Staff must belong to a branch.'
              : undefined
          }
        >
          Add staff
        </button>
      </div>

      {addOpen && (
        <AddStaffCard
          branches={branches}
          onCancel={() => setAddOpen(false)}
          onCreated={(staff, password) => {
            setAddOpen(false)
            setTempPwd({
              email: staff.email,
              name: staff.display_name,
              password,
            })
          }}
        />
      )}

      {staffRows.length === 0 ? (
        <div className="owner-card">
          <p>No staff yet. Add your first one above.</p>
        </div>
      ) : (
        <div className="owner-branches-list">
          {staffRows.map((s) => (
            <StaffCard
              key={s.id}
              staff={s}
              branchName={branches.find((b) => b.id === s.branch_id)?.name ?? '—'}
              onReset={(pwd) =>
                setTempPwd({
                  email: s.email,
                  name: s.display_name,
                  password: pwd,
                })
              }
            />
          ))}
        </div>
      )}

      {tempPwd && (
        <TempPasswordModal info={tempPwd} onClose={() => setTempPwd(null)} />
      )}
    </div>
  )
}

function AddStaffCard({
  branches,
  onCancel,
  onCreated,
}: {
  branches: OwnerBranch[]
  onCancel: () => void
  onCreated: (staff: OwnerStaffRow, password: string) => void
}) {
  const create = useCreateStaff()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  // Generate a starter password the owner can either keep or replace.
  // 16 chars URL-safe — matches the admin reset format so the owner sees a
  // consistent shape. We use Math.random for the seed (not crypto) because
  // the password is shown in plaintext and immediately changeable; a strong
  // RNG isn't load-bearing here.
  const [password, setPassword] = useState(() => quickRandomPassword())
  const [branchId, setBranchId] = useState(branches[0]?.id ?? '')

  async function submit() {
    if (!email.trim() || !displayName.trim() || password.length < 12 || !branchId) {
      toast.error('Fill in all fields. Password must be 12+ characters.')
      return
    }
    try {
      const res = await create.mutateAsync({
        email: email.trim(),
        display_name: displayName.trim(),
        password,
        branch_id: branchId,
      })
      onCreated(res.staff, password)
    } catch (err) {
      const msg =
        err instanceof OwnerApiError
          ? err.code === 'STAFF_SEAT_CAP_REACHED'
            ? "You've hit your staff seat limit."
            : err.message
          : 'Could not add staff.'
      toast.error(msg)
    }
  }

  return (
    <div className="owner-card">
      <div className="owner-card-title">Add staff</div>
      <label className="owner-field">
        <span className="owner-field-label">Display name</span>
        <input
          className="owner-input"
          name="staff_display_name"
          autoComplete="off"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoFocus
        />
      </label>
      <label className="owner-field">
        <span className="owner-field-label">Email (login)</span>
        <input
          className="owner-input"
          type="email"
          name="staff_email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label className="owner-field">
        <span className="owner-field-label">Branch</span>
        <select
          className="owner-input"
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
        >
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      <label className="owner-field">
        <span className="owner-field-label">Starter password</span>
        <input
          className="owner-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="owner-card-sub" style={{ marginTop: 4 }}>
          We'll show this password once after creating so you can send it to
          your staff.
        </p>
      </label>
      <div className="owner-form-actions">
        <button
          type="button"
          className="owner-btn"
          onClick={onCancel}
          disabled={create.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={submit}
          disabled={create.isPending}
        >
          {create.isPending ? 'Adding…' : 'Add staff'}
        </button>
      </div>
    </div>
  )
}

function StaffCard({
  staff,
  branchName,
  onReset,
}: {
  staff: OwnerStaffRow
  branchName: string
  onReset: (password: string) => void
}) {
  const del = useDeleteStaff()
  const [resetOpen, setResetOpen] = useState(false)

  async function handleDelete() {
    if (
      !window.confirm(
        `Remove ${staff.display_name}? This deletes their login permanently.`,
      )
    ) {
      return
    }
    try {
      await del.mutateAsync(staff.id)
      toast.success('Staff removed.')
    } catch {
      toast.error('Could not remove staff.')
    }
  }

  return (
    <div className="owner-card owner-branch-row">
      <div>
        <div className="owner-card-title">{staff.display_name}</div>
        <p className="owner-card-sub">
          {staff.email} · {branchName}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="owner-btn"
          onClick={() => setResetOpen(true)}
        >
          Reset password
        </button>
        <button
          type="button"
          className="owner-btn owner-btn-danger"
          onClick={handleDelete}
          disabled={del.isPending}
        >
          Remove
        </button>
      </div>
      {resetOpen && (
        <ResetPasswordModal
          staff={staff}
          onClose={() => setResetOpen(false)}
          onReset={(pwd) => {
            setResetOpen(false)
            onReset(pwd)
          }}
        />
      )}
    </div>
  )
}

function ResetPasswordModal({
  staff,
  onClose,
  onReset,
}: {
  staff: OwnerStaffRow
  onClose: () => void
  onReset: (password: string) => void
}) {
  const reset = useResetStaffPassword()
  // Prefill a suggestion the owner can keep or replace — same shape as the
  // Add-staff form.
  const [password, setPassword] = useState(() => quickRandomPassword())

  async function submit() {
    if (password.length < 12) {
      toast.error('Password must be 12+ characters.')
      return
    }
    try {
      const res = await reset.mutateAsync({ staffId: staff.id, password })
      onReset(res.temporary_password)
    } catch {
      toast.error('Could not reset password.')
    }
  }

  return (
    <div
      className="stamp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="stamp-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="stamp-modal-title">
          Reset password for {staff.display_name}
        </h2>
        <p className="owner-card-sub">
          Set a new password for this staff member. They'll need it to log in.
        </p>
        <label className="owner-field" style={{ marginTop: 12 }}>
          <span className="owner-field-label">New password</span>
          <input
            className="owner-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
        </label>
        <div className="owner-form-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="owner-btn"
            onClick={onClose}
            disabled={reset.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={submit}
            disabled={reset.isPending}
          >
            {reset.isPending ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
      </div>
    </div>
  )
}

function TempPasswordModal({
  info,
  onClose,
}: {
  info: { email: string | null; name: string; password: string }
  onClose: () => void
}) {
  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(info.password)
      toast.success('Password copied')
    } catch {
      toast.error('Copy failed. Copy manually')
    }
  }
  return (
    <div
      className="stamp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="stamp-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="stamp-modal-title">Password for {info.name}</h2>
        <p>
          Send this password to {info.email ?? 'your staff'} via Messenger or
          Viber. <strong>This is the only time we'll show it.</strong>
        </p>
        <div className="owner-staff-info-value" style={{ marginTop: 12 }}>
          <code className="owner-staff-info-code">{info.password}</code>
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={copyPassword}
          >
            Copy
          </button>
        </div>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={onClose}
          style={{ marginTop: 18, width: '100%' }}
        >
          Done
        </button>
      </div>
    </div>
  )
}

function quickRandomPassword(): string {
  const alpha =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789-_'
  let s = ''
  for (let i = 0; i < 16; i += 1) {
    s += alpha[Math.floor(Math.random() * alpha.length)]
  }
  return s
}

function StaffAccessCard({ slug }: { slug: string }) {
  const staffLoginUrl = `${window.location.origin}/staff/login`
  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copied`)
    } catch {
      toast.error('Copy failed. Please copy manually')
    }
  }
  return (
    <div className="owner-card">
      <div className="owner-card-title">Staff access</div>
      <p className="owner-card-sub">
        Share these with your staff so they can sign in to the staff scanner
        on their phone. They'll also need an email + password — create those
        below.
      </p>
      <div className="owner-staff-info-row">
        <div className="owner-staff-info-label">Business code</div>
        <div className="owner-staff-info-value">
          <code className="owner-staff-info-code">{slug}</code>
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={() => copy(slug, 'Business code')}
          >
            Copy
          </button>
        </div>
      </div>
      <div className="owner-staff-info-row">
        <div className="owner-staff-info-label">Staff login URL</div>
        <div className="owner-staff-info-value">
          <code className="owner-staff-info-code">{staffLoginUrl}</code>
          <button
            type="button"
            className="owner-btn owner-btn-sm"
            onClick={() => copy(staffLoginUrl, 'Staff login URL')}
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  )
}
